import { SxProps } from '@mui/material';
import { NotePencil } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IBomPart } from 'bom/types';
import { MenuButton } from 'core/elements';
import { IComponentNote } from 'jobs/types';
import { Color, Variant } from 'theme/enums';

import { AddPartNote } from './AddPartNote';
import { PartNoteType } from './enums';
import { IPartNote, IPartNoteForm } from './types';

export interface IAddPartButtonProps {
  level?: number;
  onSubmit?: (
    reset?: UseFormReset<IPartNoteForm>
  ) => (data: IPartNoteForm) => Promise<{ success: boolean } | void>;
  upsertPartNoteAction?: (
    data: IPartNote
  ) => Promise<{ success: boolean; partNote?: IPartNote; bomParts?: IBomPart[] }>;
  onSubmitSuccess?: (data: { partNotes?: IComponentNote[]; bomParts?: IBomPart[] }) => void;
  availableNoteTypes?: PartNoteType[] | string[];
  buttonProps?: {
    disabled?: boolean;
    color?: Color;
    variant?: Variant;
    startDecorator?: JSX.Element;
    sx?: SxProps;
  };
}

export const AddPartNoteButton = ({
  level = 0,
  onSubmit = null,
  upsertPartNoteAction = null,
  onSubmitSuccess = null,
  availableNoteTypes = Object.values(PartNoteType),
  buttonProps: customButtonProps = {},
}: IAddPartButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation();

  const handleOnClose = () => setIsOpen(false);

  const buttonProps = useMemo(
    () => ({
      disabled: false,
      color: Color.PRIMARY,
      variant: Variant.SOLID,
      startDecorator: <NotePencil />,
      sx: {},
      ...customButtonProps,
    }),
    [customButtonProps]
  );

  return (
    <>
      <MenuButton
        label={t('buttonLabelAddNotes')}
        endDecorator={null}
        onClick={() => setIsOpen(true)}
        {...buttonProps}
      />

      <AddPartNote
        isOpen={isOpen}
        level={level}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        upsertPartNoteAction={upsertPartNoteAction}
        onSubmitSuccess={onSubmitSuccess}
        availableNoteTypes={availableNoteTypes}
      />
    </>
  );
};
